import React from "react";
import classNames from "classnames";
import RadioButtonSkeletonLoading from "../radio-button-skeleton-loading/index.js";
import { RadioButton } from "@lululemon/ecom-pattern-library-radio-button";
import styles from "./bopis-accordion-nearby-stores.module.scss";
import {
  USER_COUNTRY_US,
  MAX_QUANTITY_TO_DISPLAY,
  availableForBOPISMessage
} from "./constants.js";

/**
 * @param {function} handleChangeLocations - A function to handle the change of the selected store location.
 * @param {Array<Object>} nearbyBopisStores - An array containing all nearby stores available for BOPIS (Buy Online, Pick Up In Store).
 * - `id` {string} - The unique identifier of the store.
 * - `name` {string} - The name of the store.
 * - `distanceToStore` {number} - Distance of the store from the user's location in miles.
 * - `address` {Object} - The address details of the store.
 *   - `address1` {string} - The first line of the store address.
 *   - `address2` {string|null} - The second line of the store address (if available).
 *   - `city` {string} - The city where the store is located.
 *   - `state` {string} - The state where the store is located.
 *   - `country` {string} - The country where the store is located.
 * @param {Array<Object>} selectedSkuQuantities - An array containing all selected SKU quantities.
 * - `skuId` {string} - The unique identifier of the SKU.
 * - `quantity` {string} - The selected quantity of the SKU.
 * - `bopisQuantity` {string} - The available quantity for BOPIS.
 * @param {string} selectedStoreId - The ID of the currently selected store.
 * @param {Object} userLocation - An object containing the location details of the user.
 * - `lat` {number} - The latitude of the user's location.
 * - `lng` {number} - The longitude of the user's location.
 * - `address` {string|null} - The address of the user (if available).
 * - `city` {string} - The city where the user is located.
 * - `state` {string} - The state where the user is located.
 * @param {string} userCountry - US or CA.
 * @param {Object} activeSku - The currently active SKU (selected product).
 * - `id` {string} - The unique identifier of the SKU.
 * - `skuUrl` {string} - The URL of the SKU's product page.
 * - `price` {Object} - The price details of the SKU.
 *   - `listPrice` {string} - The listed price of the product.
 *   - `onSale` {boolean} - Whether the product is on sale.
 *   - `salePrice` {string|null} - The sale price if applicable, otherwise `null`.
 * - `size` {string} - The selected size of the SKU.
 * - `color` {Object} - The color details of the SKU.
 *   - `name` {string} - The name of the color.
 *   - `code` {string} - The code of the color.
 * - `available` {boolean} - Indicates whether the SKU is available for purchase.
 * - `styleId` {string} - The style identifier of the SKU.
 * @param {function} onChangeHandler - A function to handle changes in the store selection (e.g., radio button change).
 */
const BopisAccordionNearbyStores = ({
  activeSku,
  handleChangeLocations,
  nearbyBopisStores,
  selectedStoreId,
  selectedSkuQuantities,
  userLocation,
  userCountry,
  onChangeHandler
}) => {
  const unitOfDistance = userCountry === USER_COUNTRY_US ? "miles" : "km";

  const displayUserLocation = () => {
    if (userLocation.address) {
      return `${userLocation.address} `;
    }
    return `${userLocation.city}, ${userLocation.state} `;
  };

  const activeSkuQuantity = selectedSkuQuantities?.find(
    (item) => item.skuId === activeSku?.id
  )?.bopisQuantity;

  const displayAvailableItems = () => {
    const quantity = Math.min(activeSkuQuantity, MAX_QUANTITY_TO_DISPLAY);
    return `${quantity} ${
      activeSkuQuantity > 1 ? "items" : "item"
    } available in size ${activeSku?.size}`;
  };

  const nearbyStoresList = () => {
    if (nearbyBopisStores == null || (selectedStoreId && !activeSkuQuantity)) {
      return <RadioButtonSkeletonLoading numberOfRadios={3} />;
    }

    return nearbyBopisStores.map((store, index) => {
      const isStoreSelected = selectedStoreId === store.id;
      return (
        <RadioButton
          key={store.id}
          id={`accordion-radio-${index + 1}`}
          name="bopis-accordion-radio-group"
          onChange={() => onChangeHandler(store, isStoreSelected)}
          checked={isStoreSelected}
        >
          <p
            className={classNames({
              [styles.storeName]: !isStoreSelected,
              [styles.storeNameNoMargin]: isStoreSelected
            })}
          >{`${store.name} (${store.distanceToStore} ${unitOfDistance})`}</p>
          {isStoreSelected && (
            <p className={styles.availabilityText}>{displayAvailableItems()}</p>
          )}
        </RadioButton>
      );
    });
  };

  return (
    <div className={styles.bopisAccordionContainer}>
      <p className={styles.bopisAccordionText}>
        {availableForBOPISMessage}
        <span className={styles.locationText}>{displayUserLocation()}</span>
        <button className="lll-link-primary" onClick={handleChangeLocations}>
          Change Locations
        </button>
      </p>
      <p className={styles.pickUpText}>Pick up in-store within 2 hours.</p>
      {nearbyStoresList()}
    </div>
  );
};

export default BopisAccordionNearbyStores;
